import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEye } from 'react-icons/fi';
import { getOrders, getOrder, updateOrderStatus } from '../services/api';

const statusOptions = ['placed', 'approved', 'processing', 'dispatched', 'delivered', 'cancelled', 'rejected'];

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [showDetail, setShowDetail] = useState<any>(null);
  const [statusForm, setStatusForm] = useState({ status: '', trackingNumber: '', adminRemarks: '' });

  useEffect(() => { loadOrders(); }, [page, filterStatus]);

  const loadOrders = async () => {
    try {
      const res = await getOrders({ page, pageSize: 20, status: filterStatus });
      setOrders(res.data.data);
      setTotal(res.data.totalCount);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const viewOrder = async (id: number) => {
    try {
      const res = await getOrder(id);
      const data = res.data.data;
      setShowDetail(data);
      setStatusForm({ status: data.status, trackingNumber: data.trackingNumber || '', adminRemarks: data.adminRemarks || '' });
    } catch (err) { console.error(err); }
  };

  const handleStatusUpdate = async () => {
    try {
      await updateOrderStatus(showDetail.orderId, statusForm);
      toast.success('Order updated');
      setShowDetail(null);
      loadOrders();
    } catch (err: any) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      placed: 'badge-warning', approved: 'badge-info', processing: 'badge-info',
      dispatched: 'badge-secondary', delivered: 'badge-success', cancelled: 'badge-danger', rejected: 'badge-danger'
    };
    return map[status] || 'badge-secondary';
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="flex-between mb-20">
        <div className="flex gap-10">
          <select className="form-control" style={{ maxWidth: 180 }} value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Order #</th><th>User</th><th>Type</th><th>Points</th><th>Status</th><th>City</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.orderId}>
                    <td><strong>{o.orderNumber}</strong></td>
                    <td>{o.fullName}<br /><span style={{ fontSize: 12, color: '#666' }}>{o.mobile}</span></td>
                    <td><span className="badge badge-info">{o.userType}</span></td>
                    <td>{o.totalPoints}</td>
                    <td><span className={`badge ${getStatusBadge(o.status)}`}>{o.status}</span></td>
                    <td>{o.shippingCity || '-'}</td>
                    <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td><button className="btn btn-sm btn-outline" onClick={() => viewOrder(o.orderId)}><FiEye /> View</button></td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={8} className="text-center">No orders</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ minWidth: 650 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order: {showDetail.orderNumber}</h3>
              <button className="modal-close" onClick={() => setShowDetail(null)}>&times;</button>
            </div>

            <div className="form-row mb-20">
              <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Customer</label><p>{showDetail.userName}</p></div>
              <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Mobile</label><p>{showDetail.userMobile}</p></div>
              <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Type</label><p>{showDetail.userType}</p></div>
              <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Total Points</label><p><strong>{showDetail.totalPoints}</strong></p></div>
            </div>

            <h4 className="mb-10">Items</h4>
            <div className="table-container mb-20">
              <table>
                <thead><tr><th>Item</th><th>Qty</th><th>Points/Item</th><th>Total</th></tr></thead>
                <tbody>
                  {showDetail.items?.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>{item.itemName}</td><td>{item.quantity}</td>
                      <td>{item.pointsPerItem}</td><td>{item.totalPoints}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="mb-10">Shipping</h4>
            <div className="form-row mb-20">
              <div><p>{showDetail.shippingName}</p></div>
              <div><p>{showDetail.shippingAddress}</p></div>
              <div><p>{showDetail.shippingCity}, {showDetail.shippingState} - {showDetail.shippingPinCode}</p></div>
              <div><p>Ph: {showDetail.shippingMobile}</p></div>
            </div>

            <h4 className="mb-10">Update Status</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={statusForm.status}
                  onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}>
                  {statusOptions.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Tracking Number</label>
                <input className="form-control" value={statusForm.trackingNumber}
                  onChange={(e) => setStatusForm({ ...statusForm, trackingNumber: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Admin Remarks</label>
              <textarea className="form-control" rows={2} value={statusForm.adminRemarks}
                onChange={(e) => setStatusForm({ ...statusForm, adminRemarks: e.target.value })} />
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetail(null)}>Close</button>
              <button className="btn btn-primary" onClick={handleStatusUpdate}>Update Status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
